import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight, Wifi, Wind, Shield, Sparkles, Leaf, Volume2, Gauge,
  ShieldCheck, Cpu, Plug, Award, Zap as ZapIcon, TrendingDown,
  CheckCircle2, BadgeCheck
} from "lucide-react";
import SectionLabel from "@/components/layout/SectionLabel";

const HERO_IMG = "/products/neopower_studio_uhd.png";
const PRODUCT_VIEWS = [
  { id: "front", label: "Front", src: "/products/neopower_studio_uhd.png" },
  { id: "studio", label: "Studio", src: "/products/neopower_studio_uhd.png", studio: true },
  { id: "angle", label: "3/4 angle", src: "/products/neopower_angle_clean.png" },
];
const LIFESTYLE_IMG = "/products/neopower_lifestyle.png";

const KEY_FEATURES = [
  { icon: Gauge, t: "COP up to 5.4", b: "Industry-leading efficiency rating — more hot water for less power." },
  { icon: Wind, t: "DC inverter compressor", b: "Variable-speed operation that adapts to real-time demand." },
  { icon: Leaf, t: "R290 natural refrigerant", b: "Low GWP, climate-friendly and highly efficient." },
  { icon: Volume2, t: "42 dBa silent mode", b: "Quiet enough to install almost anywhere outside the home." },
  { icon: Wifi, t: "Wi-Fi app control", b: "Free Android & iOS app — schedule, monitor, optimise from anywhere." },
  { icon: Award, t: "7-year warranty*", b: "Anti-corrosion enamel-coated steel tank — built to last." },
];

const CAPACITIES = [
  {
    size: "270L",
    model: "NA44-270B",
    suits: "Average family homes · 2–4 people",
    badge: "Available now",
    badgeColor: "bg-[#10B981] text-white",
    points: [
      "Best all-rounder for most Kiwi families",
      "Good balance of storage and efficiency",
      "Suitable for many 3–4 bedroom NZ homes",
    ],
    available: true,
  },
  {
    size: "320L",
    model: "NA45-320B",
    suits: "Larger households · 4–6 people",
    badge: "Coming soon",
    badgeColor: "bg-[#0B132B] text-white",
    points: [
      "Designed for larger households",
      "Better for high shower use & multi-bathroom homes",
      "Extra storage capacity for peak demand",
    ],
    available: false,
  },
];

export default function NeoPower() {
  const [view, setView] = useState("front");
  const activeSrc = PRODUCT_VIEWS.find((v) => v.id === view)?.src || HERO_IMG;

  return (
    <div className="bg-white">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F8FAFC]">
        <div className="absolute inset-0 bg-grid opacity-50 pointer-events-none" />
        <div className="absolute -top-32 -right-32 h-[420px] w-[420px] rounded-full bg-[#10B981]/12 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 pt-16 pb-20 sm:pt-20 sm:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <SectionLabel>Black Shield Series</SectionLabel>
            <h1 className="font-heading mt-5 text-4xl sm:text-5xl lg:text-[58px] font-bold tracking-tight text-[#0B132B] leading-[1.05]">
              NeoPower <span className="text-[#10B981]">Black Shield</span> Series
            </h1>
            <p className="mt-4 text-lg text-[#475569] leading-relaxed max-w-xl">
              Next-generation all-in-one hot water heat pumps for efficient, reliable and smarter home hot water.
            </p>
            <p className="mt-4 text-base text-[#475569] leading-relaxed max-w-xl">
              The <strong className="text-[#0B132B]">NeoPower Black Shield Series</strong> combines premium matte-black design, advanced inverter efficiency, R290 natural refrigerant, Wi-Fi app control and quiet operation in one all-in-one hot water heat pump system. Available in <strong className="text-[#0B132B]">270L and 320L</strong> capacities, the series is designed for households wanting lower running costs, reliable hot water and a smarter upgrade from traditional electric or gas hot water.
            </p>

            <div className="mt-7 grid sm:grid-cols-2 gap-3 max-w-xl">
              {[
                { t: "COP up to 5.4", sub: "Industry-leading efficiency" },
                { t: "DC Full Inverter Technology", sub: "Adaptive, quieter performance" },
              ].map((h, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-3 px-4 py-3 rounded-2xl bg-white border border-[#E2E8F0]"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#10B981] text-white shrink-0">
                    <BadgeCheck className="h-4 w-4" strokeWidth={2.5} />
                  </span>
                  <span>
                    <p className="font-heading font-semibold text-[#0B132B] text-sm leading-tight">{h.t}</p>
                    <p className="text-[11px] text-[#475569] mt-0.5">{h.sub}</p>
                  </span>
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-2.5">
              {[
                "Smarter",
                "Quieter",
                "More Refined",
              ].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#F8FAFC] border border-[#E2E8F0] text-xs font-semibold text-[#0B132B]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> {t}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/free-home-assessment" data-testid="neopower-cta" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-4 font-semibold transition-all hover:shadow-xl hover:shadow-emerald-500/30 active:scale-95">
                Request a NeoPower Assessment <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white px-7 py-4 font-semibold transition-all">
                Calculate My Savings
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              {/* Ambient glow */}
              <div className="absolute -inset-8 rounded-[2.75rem] bg-gradient-to-br from-[#10B981]/12 via-transparent to-[#0B132B]/10 blur-2xl pointer-events-none" />

              <div className="relative rounded-[2rem] bg-white border border-[#E2E8F0] shadow-[0_30px_80px_-25px_rgba(11,19,43,0.18)] overflow-hidden">
                <div className="flex items-center justify-between px-5 pt-5">
                  <div>
                    <p className="font-heading font-bold text-[15px] text-[#0B132B] leading-tight">Black Shield Series</p>
                    <p className="text-[11px] text-[#475569] mt-0.5">Premium all-in-one heat pump</p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#10B981]/10 text-[#059669] text-[10px] font-semibold tracking-widest uppercase">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-dot" /> NZ Launch
                  </span>
                </div>

                <div className="relative h-[460px] sm:h-[500px] flex items-end justify-center px-6 pt-2 pb-2 bg-gradient-to-b from-white via-[#F5F8FB] to-[#EAEFF5]">
                  {/* Floating stat pills */}
                  <div className="absolute top-6 right-5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[10px] font-semibold text-[#0B132B] shadow-sm">
                    <Gauge className="h-3 w-3 text-[#10B981]" /> COP up to 5.4
                  </div>
                  <div className="absolute top-1/2 left-4 -translate-y-1/2 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[10px] font-semibold text-[#0B132B] shadow-sm">
                    <Volume2 className="h-3 w-3 text-[#10B981]" /> 42 dBa silent
                  </div>
                  <div className="absolute top-[36%] right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-[#E2E8F0] text-[10px] font-semibold text-[#0B132B] shadow-sm">
                    <Wifi className="h-3 w-3 text-[#10B981]" /> Wi-Fi App
                  </div>
                  <div className="absolute bottom-12 right-4 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#10B981] text-white text-[10px] font-bold shadow-sm">
                    Save up to 80%
                  </div>

                  <img
                    src={activeSrc}
                    alt="NeoPower Black Shield 270L"
                    className="relative z-10 h-[420px] sm:h-[460px] w-auto object-contain drop-shadow-[0_28px_24px_rgba(11,19,43,0.32)]"
                  />
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-[60%] h-3 bg-[#0B132B]/12 blur-xl rounded-full" />
                </div>

                <div className="px-5 pt-3 pb-4 bg-white border-t border-[#E2E8F0]">
                  <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F8FAFC] border border-[#E2E8F0]">
                    {PRODUCT_VIEWS.map((v) => (
                      <button
                        key={v.id}
                        data-testid={`product-view-${v.id}`}
                        onClick={() => setView(v.id)}
                        className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                          view === v.id ? "bg-[#0B132B] text-white" : "text-[#475569] hover:text-[#0B132B]"
                        }`}
                      >
                        {v.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-3 divide-x divide-[#E2E8F0] border-t border-[#E2E8F0]">
                  <div className="px-4 py-3.5">
                    <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">COP up to</p>
                    <p className="font-heading text-xl font-bold text-[#0B132B] num-tab">5.4</p>
                  </div>
                  <div className="px-4 py-3.5">
                    <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Silent mode</p>
                    <p className="font-heading text-xl font-bold text-[#0B132B] num-tab">42 dBa</p>
                  </div>
                  <div className="px-4 py-3.5">
                    <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">Warranty*</p>
                    <p className="font-heading text-xl font-bold text-[#10B981] num-tab">7 yrs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERIES HIGHLIGHTS */}
      <section data-testid="series-highlights" className="py-16 sm:py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="max-w-3xl">
            <SectionLabel>Series highlights</SectionLabel>
            <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0B132B]">
              Designed for efficiency. Built for Kiwi homes.
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            {[
              { stat: "5.4", unit: "COP", label: "Up to", green: true },
              { stat: "80%", unit: "Save", label: "Up to" },
              { stat: "270L · 320L", unit: "Capacities", label: "Available in", small: true },
              { stat: "42", unit: "dBa", label: "Silent mode" },
              { stat: "7", unit: "Year", label: "Tank warranty*" },
              { stat: "R290", unit: "Refrigerant", label: "Natural", small: true },
            ].map((s, i) => (
              <Card key={i} className="rounded-2xl p-5 border-[#E2E8F0] bg-white hover:shadow-lg transition-all text-center">
                <p className="text-[10px] uppercase tracking-widest text-[#475569] font-semibold">{s.label}</p>
                <p className={`font-heading font-bold mt-1 num-tab ${s.small ? "text-base sm:text-lg" : "text-3xl sm:text-4xl"} ${s.green ? "text-[#10B981]" : "text-[#0B132B]"}`}>
                  {s.stat}
                </p>
                <p className="text-[11px] text-[#475569] font-medium mt-1">{s.unit}</p>
              </Card>
            ))}
          </div>
          <p className="mt-6 text-[11px] text-[#475569] max-w-3xl">
            *Actual savings vary depending on household usage, energy tariffs, climate, installation conditions and the system being replaced.
          </p>
        </div>
      </section>

      {/* SAVINGS BANNER */}
      <section className="bg-[#0B132B] text-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-12 sm:py-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-[11px] font-semibold tracking-widest uppercase">
              <TrendingDown className="h-3 w-3" /> Real savings
            </span>
            <h2 className="font-heading mt-4 text-3xl sm:text-5xl font-bold tracking-tight">
              Save up to <span className="text-[#10B981]">80%</span> on hot water energy use.
            </h2>
            <p className="mt-4 text-white/70 max-w-2xl">
              Compared with traditional electric storage or gas hot water systems, the NeoPower Black Shield's heat pump technology produces far more hot water for every unit of electricity used — meaning lower running costs, every single month.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Up to</p>
                <p className="font-heading text-4xl font-bold mt-1 text-[#10B981] num-tab">80%</p>
                <p className="text-xs text-white/60 mt-1">Energy reduction*</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">COP up to</p>
                <p className="font-heading text-4xl font-bold mt-1 num-tab">5.4</p>
                <p className="text-xs text-white/60 mt-1">Heat per kW</p>
              </div>
              <div className="rounded-2xl bg-white/5 border border-white/10 p-5 text-center">
                <p className="text-[10px] uppercase tracking-widest text-white/50 font-semibold">Warranty</p>
                <p className="font-heading text-4xl font-bold mt-1 num-tab">7</p>
                <p className="text-xs text-white/60 mt-1">Years on tank*</p>
              </div>
            </div>
            <p className="mt-4 text-[10px] text-white/40 leading-relaxed">
              *Indicative comparison vs. traditional electric storage / continuous gas systems under rated conditions. Actual savings vary by household, tariff, climate and installation.
            </p>
          </div>
        </div>
      </section>

      {/* INFOGRAPHIC: 1 kW → 5.4 kW */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <SectionLabel>How it works</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0B132B] max-w-3xl">
            How the Black Shield Heat Pump Works
          </h2>
          <p className="mt-3 text-[#475569] max-w-2xl leading-relaxed">
            Unlike a traditional electric element that creates heat directly, a heat pump captures heat from surrounding air and transfers it into the water tank. This allows the Black Shield to generate more heat energy than the electricity it consumes under suitable operating conditions.
          </p>

          {/* Step strip */}
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { n: "01", t: "Ambient air enters", b: "Outdoor air flows into the heat pump." },
              { n: "02", t: "Heat is extracted", b: "Refrigerant absorbs heat from the air." },
              { n: "03", t: "Compressor boosts", b: "Compressor increases heat energy." },
              { n: "04", t: "Heat transferred", b: "Energy moves into the water tank." },
              { n: "05", t: "Hot water ready", b: "Stored, ready for the household." },
            ].map((s, i) => (
              <Card key={i} className="p-4 rounded-2xl border-[#E2E8F0] bg-white">
                <p className="text-[11px] font-bold text-[#10B981] tracking-widest">{s.n}</p>
                <p className="font-heading text-[15px] font-semibold text-[#0B132B] mt-1">{s.t}</p>
                <p className="text-xs text-[#475569] mt-1 leading-relaxed">{s.b}</p>
              </Card>
            ))}
          </div>

          <Card className="mt-8 rounded-3xl p-6 sm:p-12 border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white">
            <div className="grid lg:grid-cols-12 gap-6 items-center">
              {/* Input */}
              <div className="lg:col-span-3 text-center">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-white border-2 border-[#E2E8F0] text-[#0B132B] mx-auto">
                  <Plug className="h-9 w-9" strokeWidth={1.8} />
                </div>
                <p className="text-[11px] uppercase tracking-widest text-[#475569] font-semibold mt-4">Power input</p>
                <p className="font-heading text-5xl font-bold text-[#0B132B] mt-1 num-tab">1<span className="text-2xl ml-1 text-[#475569] font-semibold">kW</span></p>
                <p className="text-xs text-[#475569] mt-1">Electricity</p>
              </div>

              {/* Heat pump centre with airflow lines */}
              <div className="lg:col-span-6">
                <div className="relative bg-white rounded-2xl border border-[#E2E8F0] p-6 flex flex-col items-center">
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#0B132B] text-white text-[10px] font-semibold tracking-widest uppercase">
                    Black Shield
                  </div>

                  {/* Airflow lines flowing in from left */}
                  <svg className="absolute left-2 top-1/2 -translate-y-1/2 opacity-50 hidden sm:block" width="44" height="80" viewBox="0 0 44 80" fill="none">
                    <path d="M0 14 Q 22 6, 42 14" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M0 40 Q 22 32, 42 40" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M0 66 Q 22 58, 42 66" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  {/* Heat flow out to right */}
                  <svg className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 hidden sm:block" width="44" height="80" viewBox="0 0 44 80" fill="none">
                    <path d="M2 14 Q 22 6, 44 14" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M2 40 Q 22 32, 44 40" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
                    <path d="M2 66 Q 22 58, 44 66" stroke="#EA580C" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>

                  <img src="/products/neopower_clean.png" alt="NeoPower 270L" className="h-44 sm:h-56 w-auto object-contain drop-shadow-[0_18px_18px_rgba(11,19,43,0.30)]" />
                  <div className="mt-3 grid grid-cols-3 gap-2 w-full">
                    <span className="text-center text-[10px] uppercase tracking-widest text-[#475569] font-semibold bg-[#F8FAFC] rounded-lg py-1">Compress</span>
                    <span className="text-center text-[10px] uppercase tracking-widest text-[#475569] font-semibold bg-[#F8FAFC] rounded-lg py-1">Transfer</span>
                    <span className="text-center text-[10px] uppercase tracking-widest text-[#475569] font-semibold bg-[#F8FAFC] rounded-lg py-1">Store</span>
                  </div>
                </div>
              </div>

              {/* Output */}
              <div className="lg:col-span-3 text-center">
                <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-[#10B981] text-white mx-auto shadow-lg shadow-emerald-500/30">
                  <ZapIcon className="h-9 w-9" strokeWidth={1.8} />
                </div>
                <p className="text-[11px] uppercase tracking-widest text-[#475569] font-semibold mt-4">Heat generated</p>
                <p className="font-heading text-5xl font-bold text-[#10B981] mt-1 num-tab">5.4<span className="text-2xl ml-1 text-[#475569] font-semibold">kW</span></p>
                <p className="text-xs text-[#475569] mt-1">Hot water</p>
              </div>
            </div>

            <p className="mt-8 text-xs text-[#475569] text-center max-w-3xl mx-auto leading-relaxed">
              Performance varies depending on climate, usage, installation conditions and operating mode. COP of 5.4 is measured at 20°C / 15°C test condition (water heated from 15°C to 55°C) and is a rated-condition figure — not a guaranteed real-world NZ annual COP.
            </p>
          </Card>
        </div>
      </section>

      {/* REAL-WORLD INSTALL */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <div className="relative rounded-[2rem] overflow-hidden ring-1 ring-[#E2E8F0] shadow-[0_30px_80px_-25px_rgba(11,19,43,0.20)]">
            <img
              src={LIFESTYLE_IMG}
              alt="NeoPower Black Shield installed in a Kiwi backyard"
              className="w-full h-[360px] sm:h-[520px] object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B132B]/85 via-[#0B132B]/55 to-transparent" />
            <div className="absolute inset-0 p-8 sm:p-14 flex items-end">
              <div className="max-w-xl text-white">
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-[11px] font-semibold tracking-widest uppercase">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-dot" /> Built for your backyard
                </span>
                <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.05]">
                  Outdoor-rated. <span className="text-[#10B981]">All-in-one.</span>
                </h2>
                <p className="mt-4 text-base sm:text-lg text-white/85 leading-relaxed">
                  The Black Shield is an integrated outdoor-rated heat pump and tank in a single, slim matte-black cabinet — designed to sit discreetly against your home and run quietly through the seasons.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Outdoor-rated cabinet", "R290 natural refrigerant", "42 dBa silent mode", "Wi-Fi app"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-semibold text-white">
                      <span className="h-1.5 w-1.5 rounded-full bg-[#10B981]" /> {t}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* KEY FEATURES */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <SectionLabel>Key features</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0B132B]">
            Engineered for modern Kiwi homes.
          </h2>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {KEY_FEATURES.map((f, i) => (
              <Card key={i} className="p-7 rounded-2xl border-[#E2E8F0] bg-white hover:shadow-lg transition-all">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B132B] text-white mb-4">
                  <f.icon className="h-5 w-5" />
                </span>
                <p className="font-heading text-xl font-semibold text-[#0B132B]">{f.t}</p>
                <p className="text-sm text-[#475569] mt-2 leading-relaxed">{f.b}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CAPACITY OPTIONS */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <SectionLabel>Capacity options</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0B132B]">
            Two sizes to suit every Kiwi home.
          </h2>
          <p className="mt-3 text-[#475569] max-w-2xl">
            The Black Shield Series launches with the <strong>270L</strong> — available now — and the larger <strong>320L</strong> coming soon. Register your interest to be first in line.
          </p>

          <div className="mt-12 grid md:grid-cols-2 gap-5 max-w-4xl">
            {CAPACITIES.map((p) => (
              <Card
                key={p.size}
                className={`relative rounded-3xl p-8 border-[#E2E8F0] bg-white transition-all hover:shadow-xl ${p.available ? "ring-2 ring-[#10B981]" : ""}`}
              >
                <Badge className={`absolute -top-3 left-7 rounded-full ${p.badgeColor} hover:opacity-90`}>
                  {p.badge}
                </Badge>
                <div className="flex items-baseline justify-between">
                  <p className="font-heading text-6xl font-bold text-[#0B132B] num-tab">{p.size}</p>
                  <Badge variant="outline" className="rounded-full border-[#10B981] text-[#10B981]">{p.model}</Badge>
                </div>
                <p className="mt-4 text-[#0B132B] font-semibold">{p.suits}</p>
                <ul className="mt-5 space-y-2 text-sm text-[#475569]">
                  {p.points.map((pt, j) => (
                    <li key={j} className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] mt-0.5 shrink-0" /> {pt}</li>
                  ))}
                </ul>
                <div className="mt-6 pt-5 border-t border-[#E2E8F0]">
                  {p.available ? (
                    <Link to="/free-home-assessment" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-5 py-2.5 font-semibold text-sm transition-all">
                      Request Assessment <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  ) : (
                    <Link to="/aeromax-270l" className="inline-flex items-center gap-2 rounded-full border-2 border-[#0B132B] text-[#0B132B] hover:bg-[#0B132B] hover:text-white px-5 py-2.5 font-semibold text-sm transition-all">
                      Join the Waitlist <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* SPEC TABLE */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <SectionLabel>Technical specifications</SectionLabel>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#0B132B]">Black Shield series — shared specs</h2>
          <Card className="mt-10 rounded-3xl border-[#E2E8F0] overflow-hidden bg-white">
            <div className="divide-y divide-[#E2E8F0]">
              {[
                ["Rated heating capacity", "3 kW"],
                ["Rated COP (20°C / 15°C → 55°C)", "Up to 5.4"],
                ["Refrigerant", "R290 (410 g)"],
                ["Heat exchanger", "Microchannel"],
                ["Air flow", "1000 m³/h"],
                ["Electric heater (backup)", "1.6 kW · 7.1 A"],
                ["Power supply", "220–240 V / 50–60 Hz"],
                ["Ambient temp range", "−7°C to 46°C"],
                ["Max water temperature", "75°C"],
                ["IP rating", "IPX4"],
                ["Max tank pressure", "0.85 MPa"],
                ["Warranty (tank)", "7 years*"],
              ].map(([k, v], i) => (
                <div key={i} className="grid grid-cols-2 px-6 py-4 hover:bg-[#F8FAFC] transition-colors">
                  <p className="text-sm text-[#475569]">{k}</p>
                  <p className="text-sm font-semibold text-[#0B132B] text-right num-tab">{v}</p>
                </div>
              ))}
            </div>
          </Card>
          <Card className="mt-6 rounded-2xl border-[#E2E8F0] p-6 bg-white">
            <p className="text-xs text-[#475569] leading-relaxed">
              <strong className="text-[#0B132B]">Note:</strong> Specifications, warranty terms, installation requirements and NZ suitability must be confirmed at quote stage and may be subject to supplier approval, compliance documentation and site assessment.
            </p>
          </Card>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 bg-[#0B132B] text-white">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <p className="text-xs uppercase tracking-widest text-[#10B981] font-semibold">The future of hot water is here</p>
          <h3 className="font-heading mt-3 text-3xl sm:text-4xl font-bold">
            Cutting-edge technology. Premium design. Exceptional efficiency.
          </h3>
          <p className="mt-3 text-white/70 max-w-2xl mx-auto">
            Get a free, no-obligation assessment based on your household size, current system and install location.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/free-home-assessment" className="inline-flex items-center gap-2 rounded-full bg-[#10B981] hover:bg-[#059669] text-white px-7 py-4 font-semibold">
              Request a NeoPower Assessment <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/savings-calculator" className="inline-flex items-center gap-2 rounded-full border-2 border-white/30 text-white hover:bg-white hover:text-[#0B132B] px-7 py-4 font-semibold transition-all">
              Run the Savings Calculator
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
